import { Record, List } from "immutable";
import { DateStringNumber, Nullable } from "common/records";
import { IOpeningEstimationData, OpeningEstimationRecord } from "common/records/OpeningEstimation";
import { IAnnotationData, AnnotationRecord } from "common/records/Annotation";
import { round, isNull, isUndefined } from "lodash";

// Calculates the area of an opening
export const calculateOpeningArea = (height: Nullable<number>, width: Nullable<number>): number => {
  if (isNull(height) || isNull(width) || isUndefined(height) || isUndefined(width)) {
    return 0;
  }

  return round((width * height) / 144, 2);
};

// Calculates the UI of an opening
export const calculateOpeningUI = (height: Nullable<number>, width: Nullable<number>): number => {
  if (isNull(height) || isNull(width) || isUndefined(height) || isUndefined(width)) {
    return 0;
  }

  return round(width + height, 2);
};

export interface IOpeningData {
  id: number;
  name: string;
  /**
   * The assumption is that an designator would only be set by an importer
   * Anything with a designator will be dropped during imports
   */
  designator: string;
  location: Nullable<string>;
  level: Nullable<string>;
  height: number;
  width: number;
  area: number;
  ui: number;
  kind?: string;
  sort_order: number;
  ref_id?: string;
  measurement_id?: number;

  opening_estimations?: IOpeningEstimationData[];

  annotation_id?: number;
  annotation?: IAnnotationData;

  _destroy?: boolean;

  created_at: DateStringNumber;
  updated_at: DateStringNumber;
}

export interface IOpeningRecord {
  id: number;
  name: string;
  /**
   * The assumption is that an designator would only be set by an importer
   * Anything with a designator will be dropped during imports
   */
  designator: string;
  location: string;
  level: string;
  height: number;
  width: number;
  area: number;
  ui: number;
  kind: Nullable<string>;
  sort_order: Nullable<number>;
  ref_id: Nullable<string>;
  measurement_id: Nullable<number>;

  opening_estimations: List<OpeningEstimationRecord>;

  annotation_id: Nullable<number>;
  annotation: Nullable<AnnotationRecord>;

  _destroy: Nullable<boolean>;

  created_at: Date;
  updated_at: Date;
}

export const defaultOpeningProps: IOpeningRecord = {
  id: 0,
  name: "",
  designator: "",
  location: "",
  level: "",
  height: 0,
  width: 0,
  area: 0,
  ui: 0,
  kind: null,
  sort_order: null,
  ref_id: null,
  measurement_id: null,
  opening_estimations: List<OpeningEstimationRecord>(),
  annotation_id: null,
  annotation: null,
  _destroy: null,
  created_at: new Date(),
  updated_at: new Date(),
};

export class OpeningRecord extends Record(defaultOpeningProps) implements IOpeningRecord {}

/**
 * Builds an opening identity key from a raw kind and id — the single source of truth for the
 * capitalized `${kind}:${id}` form the wizard's "Added"/used-opening lock compares. Both
 * `openingKey` (from an opening record) and `usedOpeningKeysInGroup` (from an opening estimation's
 * `openable_type`/`openable_id`) build their keys through this so the two always match. Distinct
 * from `typedOpeningKey`, which lowercases for annotation-link matching.
 *
 * @param {Nullable<string>} kind e.g. "Window" or "Door"
 * @param {number} id
 * @returns {string} e.g. "Window:3"
 */
export const openingKeyFor = (kind: Nullable<string>, id: number): string => `${kind}:${id}`;

/**
 * Stable identity key for an opening across the windows/doors stores — ids alone collide between
 * kinds (window 3 vs door 3).
 *
 * @param {Pick<IOpeningRecord, "kind" | "id">} opening
 * @returns {string} e.g. "Window:3"
 */
export const openingKey = (opening: Pick<IOpeningRecord, "kind" | "id">): string =>
  openingKeyFor(opening.kind, opening.id);

/**
 * Case-normalized identity key from a kind and id, for matching openings against annotation links
 * where the kind casing differs by source (annotation `opening_type` is lowercase "window"/"door";
 * an opening record's `kind` may be "Window"). Distinct from `openingKey`, which preserves the raw
 * casing that other call sites (windows/doors store lookups) depend on.
 *
 * @param {Nullable<string>} kind e.g. "Window" or "window"
 * @param {number} id
 * @returns {string} e.g. "window:7"
 */
export const typedOpeningKey = (kind: Nullable<string>, id: number): string => `${(kind ?? "").toLowerCase()}:${id}`;

/** A Filter Group dropdown option: the "All" un-filter default, or filter by location, level, or exact name. */
export interface IOpeningFilterOption {
  type: "all" | "location" | "level" | "name";
  name: string;
  value: string;
}

/** The un-filter default option, always first and selected by default (rsf-app "All"). */
export const ALL_OPENINGS_FILTER: IOpeningFilterOption = { type: "all", name: "All", value: "" };

/**
 * The Filter Group dropdown options for a set of openings: the "All" un-filter default first, then
 * distinct locations, levels, and names, each group sorted by label, in that order (rsf-app
 * `_setupOpenings` filters). Null/empty values are dropped.
 *
 * @param {List<IOpeningRecord>} openings
 * @returns {IOpeningFilterOption[]}
 */
export const openingFilterOptions = (openings: List<IOpeningRecord>): IOpeningFilterOption[] => {
  const collect = (type: "location" | "level" | "name", label: (value: string) => string) => {
    const values = new globalThis.Set<string>();
    openings.forEach((o) => {
      const value = o[type === "name" ? "name" : type];
      if (value) values.add(value);
    });
    return [...values]
      .map((value) => ({ type, name: label(value), value }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  return [
    ALL_OPENINGS_FILTER,
    ...collect("location", (v) => `Location: ${v}`),
    ...collect("level", (v) => `Level: ${v}`),
    ...collect("name", (v) => v),
  ];
};

/**
 * Applies a Filter Group option to an opening (rsf-app `filterOpenings`' filter branch). A null
 * filter matches everything.
 *
 * @param {IOpeningRecord} opening
 * @param {IOpeningFilterOption | null} filter
 * @returns {boolean}
 */
export const openingMatchesFilter = (opening: IOpeningRecord, filter: IOpeningFilterOption | null): boolean => {
  if (!filter) return true;
  switch (filter.type) {
    case "all":
      return true;
    case "location":
      return opening.location === filter.value;
    case "level":
      return opening.level === filter.value;
    case "name":
      return opening.name === filter.value;
    default:
      return true;
  }
};

// Group members by key in first-occurrence order (JS Map preserves insertion order); rsf-app's
// underscore groupBy behaves the same, keeping the ported output ordering identical.
const groupInOrder = <T>(items: List<T>, keyOf: (item: T) => string): globalThis.Map<string, T[]> => {
  const groups = new globalThis.Map<string, T[]>();
  items.forEach((item) => {
    const key = keyOf(item);
    const members = groups.get(key) ?? [];
    members.push(item);
    groups.set(key, members);
  });
  return groups;
};

const locationLabel = (location: string): string => (!location || location === "null" ? "" : location);

/**
 * Whether an opening's (or annotation's) Location counts as unset.
 *
 * The literal string `"null"` is blank: legacy rows carry it, and `locationLabel` / `prettyOpenings`
 * already render it as empty — a value that displays blank must not satisfy a required field.
 *
 * @param location
 * @returns {boolean} true when the location is unset, whitespace-only, or the string "null"
 */
export const isBlankLocation = (location: Nullable<string>): boolean => {
  const trimmed = location?.trim();
  return !trimmed || trimmed === "null";
};

/**
 * Opening names grouped by location: "Front: A, B | Back: C" (empty label for a null location).
 * Ports rsf-app EstimatorService.prettyOpenings.
 *
 * @param {List<IOpeningRecord>} openings
 * @returns {string}
 */
export const prettyOpenings = (openings: List<IOpeningRecord>): string => {
  const byLocation = groupInOrder(openings, (o) => String(o.location));
  return [...byLocation.entries()]
    .map(([location, members]) => `${locationLabel(location)}: ${members.map((o) => o.name).join(", ")}`)
    .join(" | ");
};

/**
 * Opening names grouped by location and W×H size with the size suffixed:
 * "Front: A, B (31x31) C (36x60) | Back: D (24x24)". Returns an error string when any opening is
 * missing a width (an unresolved openable). Ports rsf-app EstimatorService.prettyOpeningsSize.
 *
 * @param {List<IOpeningRecord>} openings
 * @returns {string}
 */
export const prettyOpeningsSize = (openings: List<IOpeningRecord>): string => {
  if (openings.some((o) => o.width === undefined)) {
    return "Error: Opening was not found.";
  }
  const byLocation = groupInOrder(openings, (o) => String(o.location));
  return [...byLocation.entries()]
    .map(([location, members]) => {
      const bySize = groupInOrder(List(members), (o) => `${o.width}x${o.height}`);
      const sizeText = [...bySize.entries()]
        .map(([size, sized]) => `${sized.map((o) => o.name).join(", ")} (${!size || size === "null" ? "" : size})`)
        .join(" ");
      return `${locationLabel(location)}: ${sizeText}`;
    })
    .join(" | ");
};

/**
 * Serializes an OpeningRecord to its raw-JSON `IOpeningData` shape. `IOpeningData` (JSON) and
 * `IOpeningRecord` (hydrated) deliberately differ — e.g. `kind`/`sort_order` nullability,
 * `created_at` Date vs DateStringNumber — so Immutable's loosely-typed `toJS()` can't infer the
 * JSON shape and needs a single boundary cast here. Mirrors `Address.toJSON` / the existing
 * annotator opening serializer; lets callers build a placeholder from `defaultOpeningProps` (the one
 * default source) instead of maintaining a parallel JSON literal.
 */
export const openingToJSON = (record: OpeningRecord): IOpeningData => record.toJS() as IOpeningData;
